import { ApolloClient, ApolloLink, HttpLink, split } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { getMainDefinition } from '@apollo/client/utilities';
import { Observable } from 'rxjs';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { get } from 'lodash';
import store, { logoutUserAction } from '../redux/store';
import { cache } from './cache';
import { showGlobalPermissionDenied } from '../contexts/PermissionDeniedContext';
import { getShareToken, isShareMode, SHARE_TOKEN_HEADER } from '../share/shareMode';
import { loginPathFor } from '../utils/loginRedirect';

const httpLink = new HttpLink({
  uri: ({ operationName, query }) => {
    const type = query.definitions.map((x) => get(x, 'operation', '')).join('');
    return `${import.meta.env.VITE_NX_SERVER}/graphql?t=${type}&o=${operationName}`;
  },
  credentials: 'include',
});

/**
 * On a share page, every request carries the link token; the server turns it into read access to
 * that one project. Mutations never leave the browser: the viewer persists view state (camera,
 * visibility, user settings) as a side effect, and a visitor has nowhere to persist it. They get
 * the same refusal the server would give, minus the round trip and the server-side error log.
 */
/** Mutations a share-link visitor may send; everything else is refused before it leaves the browser. */
const SHARE_ALLOWED_MUTATIONS = new Set(['projectExportPointcloud']);

const shareTokenLink = new ApolloLink((operation, forward) => {
  const token = getShareToken();
  if (!token) return forward(operation);
  const definition = getMainDefinition(operation.query);
  if (definition.kind === 'OperationDefinition' && definition.operation === 'mutation' && !SHARE_ALLOWED_MUTATIONS.has(operation.operationName ?? '')) {
    return new Observable((observer) => {
      observer.next({ data: null, errors: [{ message: 'FORBIDDEN', extensions: { code: 'UNAUTHENTICATED', reason: 'SHARE_LINK_DENIED' } }] });
      observer.complete();
    });
  }
  operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({ headers: { ...headers, [SHARE_TOKEN_HEADER]: token } }));
  return forward(operation);
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: `${import.meta.env.VITE_NX_SERVER?.replace('http', 'ws')}/graphql`,
  }),
);

const link = () => {
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    ApolloLink.from([
      onError(({ error, operation }) => {
        if (CombinedGraphQLErrors.is(error)) {
          const graphQLErrors = error.errors;
          graphQLErrors.forEach(({ message, locations, path }: { message: string; locations?: unknown; path?: unknown }) => console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`));
          // A share-link visitor has no session to log in to and nowhere to be redirected: anything the
          // link does not cover simply fails quietly (the viewer renders read-only without it).
          if (isShareMode()) return;
          if (graphQLErrors.find(({ extensions }: { extensions?: Record<string, unknown> }) => extensions?.code === 'UNAUTHENTICATED' && extensions?.reason === 'NO_USER')) {
            store.dispatch(logoutUserAction({}));
            // A session that expired mid-visit: sign in and come straight back to this page.
            apolloClient.cache.reset().then(() => window.location.replace(loginPathFor(window.location)));
          }
          const invalidProjectError = graphQLErrors.find(({ message }: { message: string }) => message === 'PROJECT_INVALID');
          if (invalidProjectError) {
            const organisationsIndex = window.location.pathname.indexOf('/organisations/');
            const slashAfterOrganisationIdIndex = window.location.pathname.indexOf('/', organisationsIndex + 15);
            const url = window.location.pathname.slice(0, slashAfterOrganisationIdIndex !== -1 ? slashAfterOrganisationIdIndex : undefined);
            const newPathname = url + '/invalid-project?reason=' + (invalidProjectError as { extensions?: Record<string, unknown> }).extensions?.reason;
            if (newPathname !== window.location.pathname + window.location.search) window.location.replace(url + '/invalid-project?reason=' + (invalidProjectError as { extensions?: Record<string, unknown> }).extensions?.reason);
          }

          // Handle FORBIDDEN authentication errors (permission denied). Only a refused *query* means
          // the user cannot be here at all; a refused mutation (a viewer's stray write, say) just fails.
          const operationDefinition = getMainDefinition(operation.query);
          const isQuery = operationDefinition.kind === 'OperationDefinition' && operationDefinition.operation === 'query';
          const forbiddenError = graphQLErrors.find(({ extensions }: { extensions?: Record<string, unknown> }) => extensions?.code === 'UNAUTHENTICATED' && extensions?.reason !== 'NO_USER');
          if (forbiddenError && isQuery) {
            const organisationsIndex = window.location.pathname.indexOf('/organisations/');
            if (organisationsIndex !== -1) {
              const organisationId = window.location.pathname.slice(organisationsIndex + 15).split('/')[0];
              const projectsUrl = `/organisations/${organisationId}/projects?permission-denied=true`;
              if (window.location.pathname + window.location.search !== projectsUrl) {
                window.location.replace(projectsUrl);
              }
              showGlobalPermissionDenied();
            }
          }
        }

        if (ServerError.is(error)) {
          try {
            JSON.parse((error as unknown as { bodyText?: string }).bodyText ?? '');
          } catch {
            //caught Network error: Unexpected token < in JSON at position 0 //
            error.message = 'Http Error';
          }
        }
      }),
      shareTokenLink,
      httpLink,
    ]),
  );
  return splitLink;
};

export const apolloClient = new ApolloClient({
  link: link(),
  cache,
});

apolloClient.onResetStore(async () => {
  apolloClient.setLink(link());
});
